import React, { Component } from "react";
import { Modal, Button } from "react-bootstrap";
import NoticeBody from "./NoticeBody";
import "./BroadcastQueue.css";

/**
 * The frame around every engine pop-up. Presentational: the queue decides what is current and
 * what happens after dismiss / complete.
 *
 *   <BroadcastModal item onDismiss onComplete onAck onWatched>{children}</BroadcastModal>
 *
 * - mandatory (item.behaviour === 'mandatory', escalated or not): backdrop="static", no
 *   keyboard, no close X, no Close button — the same chrome as the hand-coded mandatory
 *   surveys in Dashboard.js
 * - closable: close X, Escape, backdrop click and a Close button all call onDismiss
 * - Done appears when the item must be completed (mandatory, require_ack or must-watch) and is
 *   disabled until the acknowledgement is ticked and the video watched; onComplete returns a
 *   promise of true/false, Done is disabled while it is in flight
 * - kind=notice renders NoticeBody; other kinds render the children (phase 2/3 bodies)
 * - kind=form: the completing button reads "Submit" and is always there, mandatory or closable
 *   (a form completes only through its answers; closing a closable form is a dismiss); the
 *   queue resolves onComplete with the FormBody submit result, so a rejected form keeps the
 *   modal open with Submit usable again
 * - kind=custom: the frame never offers Done or Submit, mandatory or closable — the slot body
 *   owns its own buttons (Submit / Confirm / I Agree / Yes–No) and reports through the queue's
 *   onComplete once its own save succeeded (SPEC 7.4); the mandatory / closable chrome is as
 *   for notices (the hand-coded modals' only buttons were inside the form)
 * - branded chrome: the header carries id="nho-modal-header" as every hand-coded modal did
 *   (Dashboard.js:634,908,1076,1159,1228,1256 at 37c3f67d) so the Dashboard.css rule applies
 *   where it loads, and BroadcastQueue.css repeats the navy header / white title scoped to
 *   .broadcast-modal so the look holds without it. A custom item adds
 *   "broadcast-modal-<custom_key>"; nho_survey also adds "nho-survey-modal" (the NHO redesign
 *   rules in Dashboard.css key on it). event_registration is size lg + centered as the old
 *   YEP modal (EventRegistrationModal.js:138-142); everything else stays xl.
 * - the body may rename the frame: every child element receives onTitle(text) — the NHO slot
 *   passes its translated title, the Happiness slot "Happiness Survey <year>"; "" falls back
 *   to item.title. State is per modal instance (the queue keys the frame per item).
 * - SPEC 23.8 signing: a form with require_signature reads "Sign" instead of "Submit". Once a
 *   per-person document is signed the queue passes `signed` (the server's content): the body is
 *   replaced by a "Signed" line, the footer offers "Download copy" (when signed.pdf_url, through
 *   onDownload) and "Next" (signedNext, through onNext) or "Close" (onSignedClose) — Close is
 *   offered on a mandatory item too, since the document is done; the X stays off for mandatory.
 * - a mandatory frame carries "broadcast-modal-mandatory" (and its backdrop
 *   "broadcast-backdrop-mandatory") so it sits above the whole layout, header Clock In included.
 */

const DEFAULT_ACK_TEXT = "I have read and understood this";
const VIDEO_TYPES = ["video_file", "video_link"];

// Per-key chrome of the migrated pop-ups (the rest of the look is CSS, keyed on the classes below).
const CUSTOM_CHROME = {
  nho_survey: { className: "nho-survey-modal" },
  event_registration: { size: "lg", centered: true },
};

// Opens a publisher URL in a new tab without handing it our window (12.6).
const openNewTab = (url) => window.open(url, "_blank", "noopener");

class BroadcastModal extends Component {
  constructor(props) {
    super(props);
    const state = props.item.state || {};
    this.state = {
      acked: !!state.acknowledged_at,
      watched: !!state.media_watched_at,
      completing: false,
      title: "",   // set by the body through onTitle; "" = item.title
    };
    this.ackReported = !!state.acknowledged_at;
    this.watchedReported = !!state.media_watched_at;
  }

  componentDidMount() {
    this.mounted = true;
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  isMandatory = () => this.props.item.behaviour === "mandatory";
  isForm = () => this.props.item.kind === "form";
  isCustom = () => this.props.item.kind === "custom";
  requiresAck = () => !!this.props.item.require_ack;
  // Only a video can be watched: the server copies the item's must_watch flag onto whatever variant
  // it resolved (an image, a PDF), and a non-video variant must never gate Done.
  requiresWatch = () => {
    const media = this.props.item.media;
    return !!(media && media.must_watch && VIDEO_TYPES.includes(media.media_type));
  };
  // A custom slot completes through its own body; the frame's Done would duplicate it.
  needsDone = () => !this.isCustom() && (this.isForm() || this.isMandatory() || this.requiresAck() || this.requiresWatch());

  canComplete = () =>
    (!this.requiresAck() || this.state.acked) &&
    (!this.requiresWatch() || this.state.watched) &&
    !this.state.completing;

  onAckChange = (e) => {
    const checked = e.target.checked;
    this.setState({ acked: checked });
    if (checked && !this.ackReported) {
      this.ackReported = true;
      if (this.props.onAck) this.props.onAck();
    }
  };

  // Reported by the body (MediaRenderer) once the video has played to the end.
  onWatched = () => {
    if (!this.state.watched) this.setState({ watched: true });
    if (!this.watchedReported) {
      this.watchedReported = true;
      if (this.props.onWatched) this.props.onWatched();
    }
  };

  isSigned = () => !!this.props.signed;

  onDismiss = () => {
    // The publisher's preview (BroadcastForm) can always be closed, even for a mandatory item.
    if (this.props.preview) {
      if (this.props.onDismiss) this.props.onDismiss();
      return;
    }
    if (this.isSigned()) {
      if (!this.isMandatory() && this.props.onSignedClose) this.props.onSignedClose();
      return;
    }
    if (this.isMandatory()) return;
    if (this.props.onDismiss) this.props.onDismiss();
  };

  // The body's title (a translated NHO title, "Happiness Survey <year>"); "" restores item.title.
  onTitle = (text) => {
    const title = text === null || text === undefined ? "" : String(text);
    if (title !== this.state.title) this.setState({ title: title });
  };

  chrome = () => (this.isCustom() && CUSTOM_CHROME[this.props.item.custom_key]) || {};

  modalClassName = () => {
    const classes = ["remark-modal", "broadcast-modal"];
    if (this.isCustom() && this.props.item.custom_key) classes.push("broadcast-modal-" + this.props.item.custom_key);
    if (this.chrome().className) classes.push(this.chrome().className);
    if (this.isMandatory()) classes.push("broadcast-modal-mandatory");
    return classes.join(" ");
  };

  // Children get onTitle so a slot can rename the frame; non-element children pass through.
  body = () => React.Children.map(this.props.children, (child) =>
    React.isValidElement(child) ? React.cloneElement(child, { onTitle: this.onTitle }) : child
  );

  onDone = () => {
    if (!this.canComplete()) return;
    const { item, onComplete } = this.props;
    if (item.confirm_before_submit && !window.confirm("Submit now? You will not be able to change this afterwards.")) {
      return;
    }
    this.setState({ completing: true });
    Promise.resolve(onComplete ? onComplete() : true).then((ok) => {
      if (ok && item.after_submit_url) {
        openNewTab(item.after_submit_url);
      }
      if (this.mounted) this.setState({ completing: false });
    });
  };

  renderSigned = () => {
    const signed = this.props.signed || {};
    return (
      <div role="status" className="alert alert-success broadcast-signed">
        <strong>Signed</strong>{signed.signed_at ? " on " + signed.signed_at + (signed.timezone ? " (" + signed.timezone + ")" : "") : ""}
      </div>
    );
  };

  renderSignedFooter = () => {
    const signed = this.props.signed || {};
    return (
      <Modal.Footer className="broadcast-modal-footer">
        {signed.pdf_url ? (
          <Button variant="outline-primary" onClick={() => this.props.onDownload && this.props.onDownload(signed.pdf_url)}>Download copy</Button>
        ) : null}
        {this.props.signedNext ? (
          <Button variant="primary" onClick={this.props.onNext}>Next</Button>
        ) : (
          <Button variant="secondary" onClick={this.props.onSignedClose}>Close</Button>
        )}
      </Modal.Footer>
    );
  };

  render() {
    const { item } = this.props;
    if (this.isSigned()) {
      const mandatory = this.isMandatory();
      return (
        <Modal show={true} className={this.modalClassName()} size="xl" aria-labelledby="broadcast-modal-title"
          backdrop={mandatory ? "static" : true} keyboard={!mandatory} onHide={this.onDismiss}
          backdropClassName={mandatory ? "broadcast-backdrop-mandatory" : undefined}>
          <Modal.Header id="nho-modal-header" closeButton={!mandatory}>
            <div>
              <Modal.Title id="broadcast-modal-title">{this.state.title || item.title}</Modal.Title>
              {item.headline ? <div className="broadcast-modal-headline">{item.headline}</div> : null}
            </div>
          </Modal.Header>
          <Modal.Body>{this.renderSigned()}</Modal.Body>
          {this.renderSignedFooter()}
        </Modal>
      );
    }
    const mandatory = this.isMandatory();
    const ackLabel = item.ack_text || DEFAULT_ACK_TEXT;
    const chrome = this.chrome();

    return (
      <Modal
        show={true}
        className={this.modalClassName()}
        size={chrome.size || "xl"}
        centered={!!chrome.centered}
        aria-labelledby="broadcast-modal-title"
        backdrop={mandatory ? "static" : true}
        keyboard={!mandatory || !!this.props.preview}
        onHide={this.onDismiss}
        backdropClassName={mandatory ? "broadcast-backdrop-mandatory" : undefined}
      >
        <Modal.Header id="nho-modal-header" closeButton={!mandatory}>
          <div>
            <Modal.Title id="broadcast-modal-title">{this.state.title || item.title}</Modal.Title>
            {item.headline ? <div className="broadcast-modal-headline">{item.headline}</div> : null}
          </div>
        </Modal.Header>
        <Modal.Body>
          {item.kind === "notice" ? <NoticeBody item={item} onWatched={this.onWatched} /> : this.body()}
        </Modal.Body>
        <Modal.Footer className="broadcast-modal-footer">
          {this.requiresAck() ? (
            <div className="form-check broadcast-modal-ack">
              <input
                type="checkbox"
                className="form-check-input"
                id="broadcast-modal-ack"
                checked={this.state.acked}
                onChange={this.onAckChange}
              />
              <label className="form-check-label" htmlFor="broadcast-modal-ack">{ackLabel}</label>
            </div>
          ) : null}
          {item.cta && item.cta.url ? (
            <Button variant="outline-primary" onClick={() => openNewTab(item.cta.url)}>
              {item.cta.label}
            </Button>
          ) : null}
          {!mandatory ? (
            <Button variant="secondary" onClick={this.onDismiss}>Close</Button>
          ) : this.props.preview ? (
            <Button variant="secondary" onClick={this.onDismiss}>Close preview</Button>
          ) : null}
          {this.needsDone() ? (
            <Button variant="primary" onClick={this.onDone} disabled={!this.canComplete()}>
              {this.isForm() ? (item.require_signature ? "Sign" : "Submit") : "Done"}
            </Button>
          ) : null}
        </Modal.Footer>
      </Modal>
    );
  }
}

export default BroadcastModal;
